import { useState } from 'react'
import { Button } from '@/components/ui/button'
import type { TemplatePreview } from './model'
import { useSigningPersistence } from './persistence'

export function TemplatePreviewStatus({
  formKey,
  state,
  errorCode,
  loading,
  error,
  canRetry,
  onReload,
}: {
  formKey: string
  state?: TemplatePreview['state']
  errorCode?: string | null
  loading: boolean
  error: boolean
  canRetry: boolean
  onReload: () => void
}) {
  const { retryPreview } = useSigningPersistence()
  const [submitting, setSubmitting] = useState(false)
  const [retryFailed, setRetryFailed] = useState(false)
  const pending = state === 'pending' || submitting
  const needsAttention = state === 'needs_attention'
  const message = error
    ? 'Could not load preview status.'
    : loading
      ? 'Loading preview…'
      : pending
        ? 'Preparing preview…'
        : needsAttention
          ? 'Preview setup needs Careforce support. The previous creation attempt must be checked before retrying.'
          : errorCode === 'template_changed'
            ? 'The template changed. Import its current version to prepare the preview.'
            : state === 'failed'
              ? 'Could not prepare the preview.'
              : 'Preview has not been prepared yet.'
  return (
    <div
      role={error || state === 'failed' || needsAttention ? 'alert' : 'status'}
      className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground">
      <p>{message}</p>
      <p>You can continue configuring the fields.</p>
      {error ? (
        <Button variant="ghost" size="sm" onClick={onReload}>
          Reload preview
        </Button>
      ) : (
        canRetry &&
        !loading &&
        !pending &&
        !needsAttention &&
        errorCode !== 'template_changed' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              setSubmitting(true)
              setRetryFailed(false)
              try {
                await retryPreview(formKey)
              } catch {
                setRetryFailed(true)
              } finally {
                setSubmitting(false)
              }
            }}>
            {state === 'missing' || !state
              ? 'Prepare preview'
              : 'Retry preview'}
          </Button>
        )
      )}
      {retryFailed && (
        <p role="alert">Could not queue the preview. Please try again.</p>
      )}
    </div>
  )
}
